import {Action, createReducer} from '@ngrx/store';
import {isNullOrUndef, produceOn} from "../../common";
import {IEnterpriseState} from "../../interfaces";
import {
    actionEnterpriseAddedPrivilege,
    actionEnterpriseGeoIPsSuccess,
    actionEnterpriseRemovedPrivilege,
    actionEnterpriseStationSave,
    actionEnterpriseStationsStatusV1V2tReply,
    actionEnterpriseStationV2NamesReply,
    actionEnterpriseSuccessA,
    actionEnterpriseSuccessB,
    actionEnterpriseSuccessC,
    actionEnterpriseSuccessD,
    actionEnterpriseUpdatedApps,
    actionEnterpriseUpdatedBusiness,
    actionEnterpriseUpdatedPrivilegeDefault,
    actionEnterpriseUpdatedUser,
    actionEnterpriseUpdatedUserDetails,
    actionEnterpriseUpdatedWhitelabel,
    actionEnterpriseUpdatePrivilegeStore
} from "../actions/enterprise.actions";
import {EnumIEnterpriseBusinessCommand, EnumIEnterprisePrivilegesCommand, IEnterpriseBusinessUser} from "../../interfaces/enterprise-business-users-interface";
import {initialEnterpriseState} from "../state/enterprise.state";
import {Lib} from "../../../libs/Lib";


const featureReducer = createReducer(
    initialEnterpriseState,
    produceOn(actionEnterpriseUpdatedUser, (draft, i_enterpriseBusinessUserUpdate) => {
        switch (i_enterpriseBusinessUserUpdate.command) {
            case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_IMPORT: {
                break;
            }
            case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_EMPTY: {
                break;
            }
            case EnumIEnterpriseBusinessCommand.USER_CREATE_ACCOUNT_SAMPLE: {
                break;
            }
            case EnumIEnterpriseBusinessCommand.USER_ADD: {
                const businessUser: IEnterpriseBusinessUser = {
                    $: {
                        accessMask: i_enterpriseBusinessUserUpdate.computedAccessMask,
                        privilegeId: i_enterpriseBusinessUserUpdate.privilegeId,
                        name: i_enterpriseBusinessUserUpdate.name2,
                        businessId: i_enterpriseBusinessUserUpdate.businessId
                    }
                };
                draft.enterpriseBusinessUsers.BusinessUsers.push(businessUser);
                break;
            }

            case EnumIEnterpriseBusinessCommand.USER_ACCOUNT: {
                draft.enterpriseBusiness.Businesses.BusinessInfo.forEach((businessAccount, idx) => {
                    if (i_enterpriseBusinessUserUpdate.businessId === businessAccount.$.businessId) {
                        draft.enterpriseBusiness.Businesses.BusinessInfo.splice(idx, 1);
                    }
                });
                break;
            }

            default: {
                draft.enterpriseBusinessUsers.BusinessUsers.forEach((enterpriseBusinessUser: IEnterpriseBusinessUser, idx) => {
                    if (i_enterpriseBusinessUserUpdate.businessId === enterpriseBusinessUser.$.businessId && i_enterpriseBusinessUserUpdate.name1 === enterpriseBusinessUser.$.name) {
                        switch (i_enterpriseBusinessUserUpdate.command) {
                            case EnumIEnterpriseBusinessCommand.USER_RENAME: {
                                enterpriseBusinessUser.$.name = i_enterpriseBusinessUserUpdate.name2;
                                break;
                            }
                            case EnumIEnterpriseBusinessCommand.USER_REMOVE: {
                                draft.enterpriseBusinessUsers.BusinessUsers.splice(idx, 1);
                                break;
                            }
                            case EnumIEnterpriseBusinessCommand.USER_UPDATE_ACCESS: {
                                if (!isNullOrUndef(i_enterpriseBusinessUserUpdate.privilegeId)) enterpriseBusinessUser.$.privilegeId = i_enterpriseBusinessUserUpdate.privilegeId;
                                if (!isNullOrUndef(i_enterpriseBusinessUserUpdate.computedAccessMask)) enterpriseBusinessUser.$.accessMask = i_enterpriseBusinessUserUpdate.computedAccessMask;
                                break;
                            }
                            case EnumIEnterpriseBusinessCommand.USER_UPDATE_ACCESS_PASSWORD: {
                                if (!isNullOrUndef(i_enterpriseBusinessUserUpdate.privilegeId)) enterpriseBusinessUser.$.privilegeId = i_enterpriseBusinessUserUpdate.privilegeId;
                                if (!isNullOrUndef(i_enterpriseBusinessUserUpdate.computedAccessMask)) enterpriseBusinessUser.$.accessMask = i_enterpriseBusinessUserUpdate.computedAccessMask;
                                // if (!isNotNullUndef(i_enterpriseBusinessUserUpdate.password)) enterpriseBusinessUser.$. = i_enterpriseBusinessUserUpdate.computedAccessMask;
                                break;
                            }
                        }
                    }
                });
            }
        }
    }),

    produceOn(actionEnterpriseUpdatedBusiness, (draft, action) => {
        const idx = draft.enterpriseBusiness.Businesses.BusinessInfo.findIndex((b) => b.$.businessId === action.business.$.businessId);
        draft.enterpriseBusiness.Businesses.BusinessInfo[idx] = action.business;
    }),

    produceOn(actionEnterpriseSuccessA, (draft, action) => {
        draft.enterpriseReseller = action.enterprise;
    }),

    produceOn(actionEnterpriseSuccessB, (draft, action) => {
        draft.enterpriseAccount = action.enterprise;
    }),

    produceOn(actionEnterpriseSuccessC, (draft, action) => {
        draft.enterpriseBusiness = action.enterprise;
    }),

    produceOn(actionEnterpriseSuccessD, (draft, action) => {
        draft.enterpriseBusinessUsers = action.enterprise;
    }),

    produceOn(actionEnterpriseStationSave, (draft, action) => {
        draft.enterpriseStations.forEach((stat) => {
            if (stat.$.station_business_id === action.station_business_id) {
                if (action.values && action.values.length > 0) {
                    action.values.forEach((v) => {
                        stat.$[v.key] = v.value;
                    });
                }
            }
        });
    }),

    produceOn(actionEnterpriseStationsStatusV1V2tReply, (draft, action) => {
        /** if first time seen slice just populate store — but DEDUPE by station_business_id first.
         *  The merge path below already dedupes; this direct assign did not. The dashboard table uses
         *  station_business_id as its trackBy key, and Angular HARD-THROWS on duplicate trackBy keys
         *  (the previous object trackBy silently tolerated dup rows). A backend returning the same
         *  station twice in the initial emission would otherwise crash the grid. First occurrence wins. **/
        if (!draft.enterpriseStations || draft.enterpriseStations.length === 0) {
            const seen = new Set<string>();
            draft.enterpriseStations = action.stationArray.filter((s) => {
                const k = s?.$?.station_business_id;
                if (k == null || seen.has(k)) return false;
                seen.add(k);
                return true;
            });
            return;
        }
        /** Index the store by station_business_id ONCE. The old code ran draft.enterpriseStations.find()
         *  / findIndex() INSIDE a forEach over the incoming array — O(n*m) AND every probe crossed the
         *  Immer proxy boundary (~560k proxied lookups per emission at 750 stations, the main reducer
         *  cost). Map + Set make it O(n+m). The has()-guard keeps first-match so the result is identical
         *  to the previous find/findIndex semantics. **/
        const storeIndexByKey = new Map<string, number>();
        for (let i = 0; i < draft.enterpriseStations.length; i++) {
            const k = draft.enterpriseStations[i]?.$?.station_business_id;
            if (k != null && !storeIndexByKey.has(k)) storeIndexByKey.set(k, i);
        }
        const incomingKeys = new Set<string>();
        action.stationArray.forEach((s) => incomingKeys.add(s?.$?.station_business_id));

        /** for station pwa version 2 only, if was online before and now didn't get an update it means it went offline so we reset connections to 0.
         *  ONLY when isFinal: the stations now arrive progressively (chunk by chunk), so on a partial/intermediate emission a station simply
         *  has not been loaded YET - it is not offline. Reconciling against a partial array would wrongly blink not-yet-arrived players offline.
         *  isFinal is undefined for legacy/non progressive callers, so treat undefined as final to preserve old behaviour. **/
        if (action.isFinal !== false) {
            draft.enterpriseStations.forEach((i_stationStore) => {
                if (i_stationStore.$.version != '2') return;
                if (!incomingKeys.has(i_stationStore.$.station_business_id)) {
                    i_stationStore.$.socket = '0';
                    i_stationStore.$.connection = '0';
                    i_stationStore.$.connected = '0';
                    i_stationStore.$.wdConnected = '0';
                }
            });
        }
        /** if new station found just add it to store, else update existing station properties **/
        action.stationArray.forEach((i_actionStation) => {
            const key = i_actionStation?.$?.station_business_id;
            const idx = storeIndexByKey.get(key);
            if (idx === undefined) {
                draft.enterpriseStations.push(i_actionStation);
                storeIndexByKey.set(key, draft.enterpriseStations.length - 1);
                return;
            }
            const stationStore = draft.enterpriseStations[idx];
            for (const k in i_actionStation?.$) {
                /** dont override station names for v1 players **/
                // if (k === 'name' && !isNullOrUndefOrEmpty(stationStore.$.name)){
                //     continue;
                // }
                if (!isNullOrUndef(stationStore?.$?.[k])) {
                    stationStore.$[k] = i_actionStation.$[k];
                }
            }
        });
    }),

    produceOn(actionEnterpriseStationV2NamesReply, (draft, action) => {
        if (!draft.enterpriseStations) return;
        /** same O(n+m) Map index instead of find-in-loop (see status reply above) **/
        const storeIndexByKey = new Map<string, number>();
        for (let i = 0; i < draft.enterpriseStations.length; i++) {
            const k = draft.enterpriseStations[i]?.$?.station_business_id;
            if (k != null && !storeIndexByKey.has(k)) storeIndexByKey.set(k, i);
        }
        action.stationArray.forEach((i_station) => {
            const key = i_station?.$?.station_business_id;
            const idx = storeIndexByKey.get(key);
            const station = idx === undefined ? undefined : draft.enterpriseStations[idx];
            if (station?.$?.version == '1') return;
            if (station) {
                station.$.name = i_station.$.name;
            } else {
                draft.enterpriseStations.push(i_station);
                storeIndexByKey.set(key, draft.enterpriseStations.length - 1);
            }
        });
    }),

    produceOn(actionEnterpriseGeoIPsSuccess, (draft, action) => {
        draft.enterpriseStationGeoIPs = action.stationGeoIps;
    }),

    // produceOn(actionLoadTrackingStationSuccess, (draft, action) => {
    //     draft.enterpriseStationTrack = action.stationTrackReply;
    // }),
    //
    // produceOn(actionLoadTrackingStationStatsSuccess, (draft, action) => {
    //     draft.enterpriseStationTrackStats = action.stationTrackReply;
    // }),

    produceOn(actionEnterpriseUpdatedUserDetails, (draft, action) => {
        draft.enterpriseAccount = action.userDetails;
    }),

    produceOn(actionEnterpriseUpdatePrivilegeStore, (draft, action) => {
        switch (action.enterprisePrivilegesUpdate.command) {
            case EnumIEnterprisePrivilegesCommand.UPDATE_PRIVILEGES_NAME: {
                draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.forEach((v) => {
                    if (v.$.id === action.enterprisePrivilegesUpdate.privilegeId)
                        v.$.name = action.enterprisePrivilegesUpdate.selPrivName;
                });
                break;
            }
            case EnumIEnterprisePrivilegesCommand.UPDATE_PRIVILEGES_ATTRIBUTES: {
                draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.forEach((v) => {
                    if (v.$.id === action.enterprisePrivilegesUpdate.privilegeId) {
                        v.Groups[0].Group.forEach((g) => {
                            if (g.$.name === action.enterprisePrivilegesUpdate.tableName) {
                                g.$[action.enterprisePrivilegesUpdate.privelegesAttribute] = action.enterprisePrivilegesUpdate.attr;
                            }
                        });
                    }
                });
                break;
            }
            case EnumIEnterprisePrivilegesCommand.UPDATE_PRIVILEGES_VALUES: {
                draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.forEach((v) => {
                    if (v.$.id === action.enterprisePrivilegesUpdate.privilegeId) {
                        v.Groups[0].Group.forEach((g) => {
                            if (g.$.name === action.enterprisePrivilegesUpdate.tableName) {
                                const key: string = action.enterprisePrivilegesUpdate.privItemKey;
                                g.Tables[0].$[key] = action.enterprisePrivilegesUpdate.updTotalBits;
                            }
                        });
                    }
                });
                break;
            }
        }
    }),

    produceOn(actionEnterpriseUpdatedApps, (draft, action) => {
        draft.enterpriseReseller.User.BusinessInfo[0].InstalledApps[0].App.forEach((v) => {
            if (v.$.id === action.id) v.$.installed = action.installed;
        });
    }),

    produceOn(actionEnterpriseUpdatedPrivilegeDefault, (draft, action) => {
        draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].$.defaultPrivilegeId = action.enterprisePrivilegesUpdate.privilegeId;
    }),

    produceOn(actionEnterpriseAddedPrivilege, (draft, action) => {
        draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.push(action.enterprisePrivilegesUpdate.privilegeData);
    }),

    produceOn(actionEnterpriseUpdatedWhitelabel, (draft, action) => {
        draft.enterpriseReseller.User.BusinessInfo[0].$.name = action.whiteLabel?.companyName;
        draft.enterpriseReseller.User.BusinessInfo[0].WhiteLabel[0] = action?.whiteLabel;
        draft.enterpriseReseller.User.BusinessInfo[0].Themes[0].$.defaultThemeId = action?.whiteLabel?.defaultThemeId;
    }),

    produceOn(actionEnterpriseRemovedPrivilege, (draft, action) => {
        draft.enterpriseBusinessUsers.BusinessUsers.forEach((user) => {
            if (user.$.privilegeId === action.enterprisePrivilegesUpdate.privilegeId) {
                user.$.privilegeId = action.enterprisePrivilegesUpdate.privilegeDefaultId;
            }
        });
        draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.forEach((v, idx) => {
            if (v.$.id === action.enterprisePrivilegesUpdate.privilegeId) {
                draft.enterpriseReseller.User.BusinessInfo[0].Privileges[0].Privilege.splice(idx, 1);
            }
        });
    })
);

export function enterpriseReducers(state: IEnterpriseState | undefined, action: Action): IEnterpriseState {
    return featureReducer(state, action);
}

